import React, {
    useState,
    useEffect,
    FC,
    useCallback,
    useMemo,
    useRef,
} from 'react';
import {
    HttpError,
    IResourceComponentsProps,
    useNavigation,
} from '@refinedev/core';
import { Form, Row, Col, Spin } from 'antd';
import {
    FormFields,
    IFormValues,
} from 'interfaces/preliminary-application.interface';
import { useParams } from 'react-router-dom';
import { TranslatedBreadcrumbs } from '../../../components/translated-breadcrumbs';
import { useTranslation } from 'react-i18next';
import { UploadFile } from 'antd/lib/upload/interface';
import { useNotification } from '../../../hooks/useNotification';
import dayjs from 'dayjs';
import { FormInstance } from 'antd/es/form';
import { handleFormError } from '../../../utils/error-handlers';
import { isValidValue } from '../../../utils/obj';
import { pickBy } from 'lodash';
import { sleep } from '../../../utils/sleep';
import { mapStoreToFormValues } from '../../../utils/mapStoreToFormValues';
import { useForm } from '@refinedev/antd';
import { ListButton } from '../../../components/ui/list-button';
import { InternalLayout } from '../../../layouts/InternalLayout';
import { StepNavigation } from 'components/preliminary-application/steps/StepNavigation';
import { Step1DrugInfo } from 'components/preliminary-application/steps/Step1DrugInfo';
import { Step2CertificateProcedure } from 'components/preliminary-application/steps/Step2CertificateProcedure';
import { Step3GeneralInfo } from 'components/preliminary-application/steps/Step3GeneralInfo';
import { Step4Documents } from 'components/preliminary-application/steps/Step4Documents';
import { Step5Submission } from 'components/preliminary-application/steps/Step5Submission';
import { FormItem, Input } from 'components/ui';
import stepStyles from 'components/preliminary-application/steps/steps.module.scss';
import styles from './edit.module.scss';
import { submitPreliminaryApplicationRequest } from '../hooks/useSubmissionRequest';
import { useStepNavigator, FIELD_STEP_MAP } from '../hooks/useStepNavigator';
import { usePageTitle } from '../hooks/usePageTitle';
import { handleDecSentState } from '../utils/handleDecSentState';

const LOADER_DELAY_IN_MS: number = 2000;

export const PreliminaryApplicationEdit: FC<IResourceComponentsProps> = () => {
    const { msgSuccess, msgError } = useNotification();
    const { id } = useParams();
    const { t } = useTranslation();
    const { replace } = useNavigation();

    const { formProps, formLoading: isLoading } = useForm<
        IFormValues,
        HttpError
    >({
        action: 'edit',
        resource: 'preliminary-request',
        queryOptions: { retry: false },
        id,
    });

    const form = formProps?.form as FormInstance | undefined;
    const initialValues = formProps?.initialValues;

    const totalSteps = 6;
    const [formValuesState, setFormValuesState] = useState<
        IFormValues | undefined
    >(undefined);
    const [isReadOnlyForm, setIsReadOnlyForm] = useState<boolean>(true);
    const [fileList, setFileList] = useState<UploadFile[]>([]);
    const [files, setFiles] = useState<any[]>([]);
    const [disSubmit, setDisSubmit] = useState(false);
    const [isFormProcessing, setIsFormProcessing] = useState<boolean>(false);
    const [isDecSent, setIsDecSent] = useState<boolean>(false);
    const [currentStep, setCurrentStep] = useState<number>(totalSteps - 1);
    const formSnapshotRef = useRef<Record<string, any>>({});

    const updateSnapshot = useCallback((values: Record<string, any>) => {
        formSnapshotRef.current = {
            ...formSnapshotRef.current,
            ...values,
        };
    }, []);

    useEffect(() => {
        setCurrentStep(totalSteps - 1);
        setIsDecSent(false);
        setDisSubmit(false);
    }, [id, totalSteps]);

    useEffect(() => {
        if (initialValues) {
            const mapped = mapStoreToFormValues(initialValues);
            const { reject_reason, ...rest } = mapped;
            const normalizedMapped = {
                ...rest,
                reject_reason: reject_reason ?? '',
            };
            setFormValuesState(normalizedMapped);
            setIsReadOnlyForm(true);
            setCurrentStep(totalSteps - 1);
        }
    }, [initialValues, totalSteps]);

    const formValues = formValuesState;

    // Static title using only URL ID - no updates to avoid visual transitions
    const pageTitle = usePageTitle({ id, t });

    const normalizedInitialValues = useMemo(() => {
        if (!formValues) {
            return undefined;
        }

        const manufacturerField = formValues?.manufacturer_id;
        let manufacturerIds: number[] = [];

        if (Array.isArray(manufacturerField)) {
            manufacturerIds = manufacturerField.map((manufacturer: any) =>
                typeof manufacturer === 'object'
                    ? manufacturer?.id
                    : manufacturer
            );
        } else if (manufacturerField) {
            manufacturerIds = [manufacturerField as any];
        }

        const regDateValue = formValues?.reg_date
            ? dayjs.isDayjs(formValues.reg_date)
                ? formValues.reg_date
                : dayjs(formValues.reg_date, 'DD.MM.YYYY')
            : null;

        return {
            ...formValues,
            reg_date: regDateValue,
            manufacturer_id: manufacturerIds,
        };
    }, [formValues]);

    useEffect(() => {
        if (normalizedInitialValues && form) {
            form.setFieldsValue(normalizedInitialValues);
            updateSnapshot(normalizedInitialValues);
        }
    }, [form, normalizedInitialValues, updateSnapshot]);

    const syncStateBeforeNavigation = useCallback(() => {
        if (!form) return;

        const currentValues = form.getFieldsValue(true) ?? {};
        updateSnapshot(currentValues);
    }, [form, updateSnapshot]);

    const { redirectToErrorStep } = useStepNavigator({
        currentStep,
        totalSteps,
        setStep: setCurrentStep,
        onBeforeStepChange: syncStateBeforeNavigation,
        fieldStepMap: FIELD_STEP_MAP,
    });

    const goNext = useCallback(() => {
        syncStateBeforeNavigation();
        setCurrentStep(prev => Math.min(prev + 1, totalSteps - 1));
    }, [syncStateBeforeNavigation, totalSteps]);

    const goPrevious = useCallback(() => {
        if (currentStep === totalSteps - 1 && isReadOnlyForm) {
            setIsReadOnlyForm(false);
        }

        syncStateBeforeNavigation();
        setCurrentStep(prev => Math.max(prev - 1, 0));
    }, [currentStep, totalSteps, isReadOnlyForm, syncStateBeforeNavigation]);

    const handleSaveAction = useCallback(() => {
        form?.submit();
    }, [form]);

    useEffect(() => {
        if (initialValues?.file) {
            setFileList(initialValues.file);
            form?.setFieldsValue({ files: initialValues.file });
        }

        if (initialValues?.files) {
            setFiles(initialValues?.files);
        }
    }, [initialValues, form]);

    useEffect(() => {
        handleDecSentState({
            initialValues,
            setIsDecSent,
            setDisSubmit,
            setIsReadOnlyForm,
        });
    }, [initialValues]);

    const handleFileChange = useCallback(
        (newFileList: UploadFile[]) => {
            setFileList(newFileList);
            form?.setFieldsValue({ files: newFileList });
        },
        [form]
    );

    const resetStatesHandler = (
        fields?: FormFields,
        formInstance?: FormInstance<IFormValues>
    ): void => {
        setIsFormProcessing(false);
        setDisSubmit(false);
        setFileList([]);

        const formToUse = formInstance ?? form;
        if (formToUse) {
            if (fields) {
                formToUse.resetFields(fields);
            } else {
                formToUse.resetFields();
            }
        }
    };

    const handleFinish = async (values: IFormValues) => {
        try {
            setIsFormProcessing(true);

            const currentValues = form?.getFieldsValue(true) ?? {};
            const mergedValues: IFormValues = {
                ...(normalizedInitialValues ?? {}),
                ...formSnapshotRef.current,
                ...currentValues,
                ...values,
            };

            const payload = {
                ...mergedValues,
                reg_date: mergedValues?.reg_date
                    ? dayjs(mergedValues?.reg_date).format('DD.MM.YYYY')
                    : null,
                _method: 'PUT',
            };
            const { application_notes, ...payloadWithoutNotes } = payload;
            const filteredValues = pickBy(payloadWithoutNotes, isValidValue);

            const data = await submitPreliminaryApplicationRequest({
                url: `/api/preliminary-request/${id}`,
                payload: filteredValues,
                fileList,
                form: form!,
                resetStates: (fields, formInstance) =>
                    resetStatesHandler(fields, formInstance),
                loaderDelay: LOADER_DELAY_IN_MS,
            });

            await sleep(LOADER_DELAY_IN_MS);
            setFiles(data?.files);
            form?.setFieldsValue(mergedValues);
            updateSnapshot(mergedValues);
            setFormValuesState(
                prev => ({ ...(prev ?? {}), ...mergedValues } as IFormValues)
            );
            resetStatesHandler(['files'], form);
            msgSuccess(
                t(
                    'notifications.the_upload_is_complete_wait_for_the_successful_processing_status_check_the_application_processing_status_after_a_while'
                )
            );

            setIsReadOnlyForm(false);
            setCurrentStep(totalSteps - 1);
            setIsFormProcessing(false);
        } catch (error: any) {
            console.error(error);

            // Handle errors with automatic step navigation
            const errorFields = handleFormError({
                e: error,
                form: form!,
                setIsFormProcessing,
                setDisSubmit: () => null,
                msgError,
                t,
                navigateToStep: (step: number) => {
                    setCurrentStep(step);
                    setIsReadOnlyForm(false);
                },
            });

            // Fallback: use redirectToErrorStep if automatic navigation didn't work
            if (errorFields?.length) {
                redirectToErrorStep(errorFields);
            }

            setIsReadOnlyForm(false);
        }
    };

    const handleSendToDec = useCallback(async () => {
        try {
            setIsFormProcessing(true);

            if (!id) {
                setIsFormProcessing(false);
                return;
            }

            const data = await submitPreliminaryApplicationRequest({
                url: `/api/preliminary-request/${id}/send`,
                payload: {},
                fileList: [],
                form: form!,
                resetStates: () => undefined,
                loaderDelay: LOADER_DELAY_IN_MS,
            });

            const state = data?.state?.name;
            if (state) {
                form?.setFieldsValue?.({ state_id: state });
            }

            await sleep(LOADER_DELAY_IN_MS);
            setIsFormProcessing(false);
            setIsReadOnlyForm(true);
            setDisSubmit(true);
            setIsDecSent(true);
            msgSuccess(t('notifications.sent_to_the_dec'));
            setCurrentStep(totalSteps - 1);
        } catch (error) {
            console.error(error);
            msgError(t('notifications.entry_error'));
            setIsFormProcessing(false);
            setIsReadOnlyForm(false);
            setDisSubmit(false);
            setIsDecSent(false);
        }
    }, [
        form,
        id,
        msgError,
        msgSuccess,
        setCurrentStep,
        setIsFormProcessing,
        setIsReadOnlyForm,
        totalSteps,
        t,
    ]);

    const headerActions = (
        <div className={styles.headerActions}>
            <ListButton
                key="listBtnKey"
                resource="preliminary-applications"
                label={t(
                    'preliminary-applications.preliminary-applications',
                    'EV Applications'
                )}
            />
        </div>
    );

    return (
        <InternalLayout
            breadcrumbs={<TranslatedBreadcrumbs />}
            onBack={() => replace('/preliminary-applications')}
            title={pageTitle}
            extra={headerActions}
            step={{
                current: currentStep + 1,
                total: totalSteps,
                label: `Крок ${currentStep + 1} із ${totalSteps}`,
            }}
        >
            <Spin spinning={isLoading || isFormProcessing}>
                <Form
                    {...formProps}
                    initialValues={normalizedInitialValues}
                    className={styles.formContainer}
                    layout="vertical"
                    onFinish={handleFinish}
                    disabled={
                        isFormProcessing ||
                        isDecSent ||
                        (isReadOnlyForm && currentStep !== totalSteps - 1)
                    }
                    preserve
                    onValuesChange={() => {
                        if (!form) return;
                        const nextValues = form.getFieldsValue(true) ?? {};
                        updateSnapshot(nextValues);
                        setFormValuesState(
                            prev =>
                                ({
                                    ...(prev ?? {}),
                                    ...nextValues,
                                } as IFormValues)
                        );
                    }}
                >
                    <Form.Item name="manufacturer_id" hidden>
                        <input type="hidden" />
                    </Form.Item>
                    <Form.Item name="state_id" hidden>
                        <input type="hidden" />
                    </Form.Item>

                    <div className={styles.stepsWrapper}>
                        {currentStep === 0 && <Step1DrugInfo />}
                        {currentStep === 1 && <Step2CertificateProcedure />}
                        {currentStep === 2 && <Step3GeneralInfo />}
                        {currentStep === 3 && (
                            <Step4Documents
                                fileList={fileList}
                                onFileChange={handleFileChange}
                            />
                        )}
                        {currentStep === 4 && (
                            <div className={stepStyles.stepContainer}>
                                <h2 className={stepStyles.stepTitle}>
                                    {t(
                                        'preliminary-applications.sections.general_form',
                                        'Загальна форма'
                                    )}
                                </h2>
                                <Row gutter={[16, 20]}>
                                    <Col xs={24} md={12}>
                                        <FormItem name="reg_num">
                                            <Input
                                                label={t(
                                                    'preliminary-applications.fields.reg_num'
                                                )}
                                            />
                                        </FormItem>
                                    </Col>

                                    <Col xs={24} md={12}>
                                        <FormItem shouldUpdate>
                                            {({ getFieldValue }) => (
                                                <Input
                                                    label={t(
                                                        'preliminary-applications.fields.state'
                                                    )}
                                                    value={
                                                        initialValues?.state
                                                            ?.name ??
                                                        getFieldValue(
                                                            'state_id'
                                                        ) ??
                                                        t('draft')
                                                    }
                                                    readOnly
                                                    disabled
                                                    style={{
                                                        fontSize: '12px',
                                                        padding: '9.5px 0',
                                                    }}
                                                />
                                            )}
                                        </FormItem>
                                    </Col>
                                </Row>
                            </div>
                        )}
                        {currentStep === 5 && (
                            <Step5Submission
                                fileList={fileList}
                                existingFiles={fileList.length > 0 ? [] : files}
                                checkboxesPosition="afterFiles"
                                showApplicationNotes
                                applicationNotesLabel={t(
                                    'preliminary-applications.fields.application_notes',
                                    'Примітки до заяви'
                                )}
                                applicationNotesInputLabel={t(
                                    'preliminary-applications.fields.note',
                                    'Зауваження'
                                )}
                            />
                        )}
                    </div>

                    <StepNavigation
                        currentStep={currentStep}
                        totalSteps={totalSteps}
                        onPrevious={goPrevious}
                        onNext={goNext}
                        onSave={handleSaveAction}
                        sendDec={handleSendToDec}
                        confirmSendDec
                        isProcessing={isFormProcessing}
                        disabled={isDecSent}
                        disSubmit={disSubmit}
                    />
                </Form>
            </Spin>
        </InternalLayout>
    );
};
